import { DiscardChangeModal } from '@/components';
import AppSaveBar from '@/components/AppSaveBar/AppSaveBar';
import BoldSmallText from '@/components/BoldSmallText';
import Layout from '@/components/layout';
import SkeletonLayout from '@/components/SkeletonLayout';
import { SkeletonWrapper } from '@/components/SkeletonLayout/styled';
import { config } from '@/config';
import { PATH } from '@/constants/path';
import { arrayToStringByComma, handleScrollToElement, openErrorToast, openToast, stringToArrayByComma } from '@/helpers';
import { checkDirtyField } from '@/helpers/compare';
import {
  useGetEmailSettingsQuery,
  useGetGeneralSettingQuery,
  useUpdateEmailSettingsMutation,
  useUpdateGeneralSettingMutation,
} from '@/redux/query';
import { handleResetErrorFields, handleSetErrorFields } from '@/redux/slice/error.slice';
import { handleResetSaveBar, handleSetSaveBar, saveBarSelector } from '@/redux/slice/savebar.slice';
import {
  generalBackupSettingSelector,
  generalSettingSelector,
  handleResetSettings,
  handleSaveBackupSettings,
  handleSaveSettings,
} from '@/redux/slice/setting.slice';
import { BlockStack, Box, Button, Card, InlineStack } from '@shopify/polaris';
import { ButtonPressIcon, DeliveryIcon, NotificationIcon, OrdersStatusIcon, SettingsIcon } from '@shopify/polaris-icons';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import AdvanceSettings from './component/AdvanceSettings';
import CheckoutValidation from './component/CheckoutValidation';
import DeliveryValidation from './component/DeliveryValidation';
import EmailNotification from './component/EmailNotification';
import OrderManagement from './component/OrderManagement';
import { StyledAvatar, StyledFlexFullWidth, StyledNav } from './styled';
import {
  emailBackupSettingSelector,
  emailSettingSelector,
  handleResetEmailSetting,
  handleSaveEmailBackupSettings,
  handleSaveEmailSettings,
} from '@/redux/slice/email.slice';
import { REGEX_EMAIL } from '@/constants';
import { IParamsApi } from '@/types/apis/params';
import { EmailVerifiedModal } from './component/Modal/EmailVerifiedModal';
import useTitleBar from '@/hooks/useTitleBar';

const GeneralSetting = () => {
  const { t } = useTranslation('general_settings');
  const dispatch = useDispatch();
  const saveBar = useSelector(saveBarSelector);
  const generalSettings = useSelector(generalSettingSelector);
  const generalBackupSettings = useSelector(generalBackupSettingSelector);
  const location = useLocation();
  const {
    data: generalSettingData,
    isLoading: isLoadingGetGeneralSetting,
    refetch: refetchGetGeneralSetting,
  } = useGetGeneralSettingQuery({ shop: config.shop }, { refetchOnMountOrArgChange: true });
  const {
    data: emailSettingData,
    refetch: refetchGetEmailSettings,
    isLoading: isLoadingEmailSetting,
    isFetching: isFetchingEmailSetting,
  } = useGetEmailSettingsQuery(undefined, { skip: location.pathname !== PATH.SETTINGS__EMAIL.pathname });
  const [updateGeneralSetting] = useUpdateGeneralSettingMutation();
  const [updateEmailSetting, { isLoading: isUpdatingEmailSettings }] = useUpdateEmailSettingsMutation();
  const navigate = useNavigate();
  const [modal, setModal] = useState({
    isOpenDiscardModal: false,
    isOpenVerifiedEmailModal: false,
  });
  const [navigatePath, setNavigatePath] = useState<string>('');

  const isChangeData = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { googleMapApiKey, googleRoutesApiKey, ...oldGeneralSettings } = generalSettings;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      googleMapApiKey: currentGoogleMapApiKey,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      googleRoutesApiKey: currentgoogleRoutesApiKey,
      ...currentGeneralSettings
    } = generalBackupSettings;
    return checkDirtyField(oldGeneralSettings, currentGeneralSettings).isDirty;
  }, [generalSettings, generalBackupSettings]);

  const currentEmailSetting = useSelector(emailSettingSelector);
  const oldEmailSetting = useSelector(emailBackupSettingSelector);

  const isChangeEmailData = useMemo(() => {
    return checkDirtyField(currentEmailSetting, oldEmailSetting).isDirty;
  }, [currentEmailSetting, oldEmailSetting]);

  const discardChange = () => {
    dispatch(
      handleSetSaveBar({
        isShowSaveBar: false,
        isLoadingSaveBar: false,
        isDisableSaveBar: false,
        isCheckSave: false,
        isDiscard: true,
      }),
    );
    dispatch(handleResetErrorFields());
    dispatch(handleResetSettings());
    dispatch(handleResetEmailSetting());
  };

  const handleSaveGeneralSettings = () => {
    if (isChangeData) {
      dispatch(handleSetSaveBar({ isLoadingSaveBar: true }));
      const dataGeneralSetting = {
        shop: config.shop,
        dateTimeOnPackingSlip: generalSettings.dateTimeOnPackingSlip,
        includeNonWorkDayInPrep: generalSettings.includeNonWorkDayInPrep,
        isRequiredBeforeCheckout: generalSettings.isRequiredBeforeCheckout,
        isShowOnProductPage: generalSettings.isShowOnProductPage,
        kindOfOverrideRules: generalSettings.kindOfOverrideRules,
        showMapOnWidget: generalSettings.showMapOnWidget,
        timezone: generalSettings.timezone,
        isEnableOrderTag: generalSettings.isEnableOrderTag,
        isAutoFillPickupAddress: generalSettings.isAutoFillPickupAddress,
        isModifiedPickupAddress: generalSettings.isModifiedPickupAddress,
        isChangeMethodShipping: generalSettings.isChangeMethodShipping,
        isDisableCheckoutButton: generalSettings.isDisableCheckoutButton,
        isDisableAddToCartButton: generalSettings.isDisableAddToCartButton,
        manualSelectorBlockCheckoutButton: stringToArrayByComma(generalSettings.manualSelectorBlockCheckoutButton.trim()),
        manualSelectorBlockAddToCartButton: stringToArrayByComma(generalSettings.manualSelectorBlockAddToCartButton.trim()),
        deliveryRestrictionType: generalSettings.deliveryRestrictionType,
        restrictionDistanceUnit: generalSettings.deliveryDistanceUnit,
        isEnableGoogleMap: generalSettings.isEnableGoogleMap,
        orderTag: generalSettings.orderTag,
        isAutoFillDeliveryZipCode: generalSettings.isAutoFillDeliveryZipCode,
      };

      updateGeneralSetting(dataGeneralSetting)
        .unwrap()
        .then(() => {
          dispatch(handleResetSaveBar());
          openToast(dispatch);
          refetchGetGeneralSetting();
        })
        .catch(() => {
          dispatch(handleResetSaveBar());
          openErrorToast(dispatch);
        });
    }

    if (isChangeEmailData) {
      dispatch(handleSetSaveBar({ isDiscard: false }));

      if (REGEX_EMAIL.test(currentEmailSetting.sender)) {
        updateEmailSetting(currentEmailSetting.fieldsChange as IParamsApi.IUpdateEmailSettings)
          .unwrap()
          .then(() => {
            refetchGetEmailSettings().then((res) => {
              if (currentEmailSetting.fieldsChange?.sender && !res.data?.data.isVerifyEmail) {
                setModal((prev) => ({ ...prev, isOpenVerifiedEmailModal: true }));
              }
            });
            openToast(dispatch);
          })
          .catch(() => {
            openErrorToast(dispatch);
          });
      } else {
        handleScrollToElement('brand-settings');
        dispatch(handleSaveEmailSettings({ isErrorEmail: true }));
      }
    }
  };

  useEffect(() => {
    if (generalSettingData?.data) {
      const generalSetting = {
        shop: config.shop,
        googleMapApiKey: generalSettingData?.data.googleMapApiKey,
        isRequiredBeforeCheckout: generalSettingData?.data.isRequiredBeforeCheckout,
        isShowOnProductPage: generalSettingData?.data.isShowOnProductPage,
        showMapOnWidget: generalSettingData?.data.showMapOnWidget,
        dateTimeOnPackingSlip: generalSettingData?.data.dateTimeOnPackingSlip,
        includeNonWorkDayInPrep: generalSettingData?.data.includeNonWorkDayInPrep,
        kindOfOverrideRules: generalSettingData?.data.kindOfOverrideRules,
        timezone: generalSettingData?.data.timezone,
        isEnableOrderTag: generalSettingData?.data.isEnableOrderTag,
        isAutoFillPickupAddress: generalSettingData?.data.isAutoFillPickupAddress,
        isModifiedPickupAddress: generalSettingData?.data.isModifiedPickupAddress,
        isChangeMethodShipping: generalSettingData?.data.isChangeMethodShipping,
        isDisableCheckoutButton: generalSettingData?.data.isDisableCheckoutButton,
        isDisableAddToCartButton: generalSettingData?.data.isDisableAddToCartButton,
        manualSelectorBlockCheckoutButton: arrayToStringByComma(generalSettingData?.data.manualSelectorBlockCheckoutButton || []),
        manualSelectorBlockAddToCartButton: arrayToStringByComma(
          generalSettingData?.data.manualSelectorBlockAddToCartButton || [],
        ),
        deliveryRestrictionType: generalSettingData?.data.deliveryRestrictionType,
        deliveryDistanceUnit: generalSettingData?.data.restrictionDistanceUnit,
        googleRoutesApiKey: generalSettingData?.data.googleRoutesApiKey,
        isEnableGoogleMap: generalSettingData?.data.isEnableGoogleMap,
        displayOnPage: generalSettingData?.data.displayOnPage,
        orderTag: generalSettingData?.data.orderTag || [],
        isAutoFillDeliveryZipCode: generalSettingData?.data.isAutoFillDeliveryZipCode,
      };
      dispatch(handleSaveSettings(generalSetting));
      dispatch(handleSaveBackupSettings(generalSetting));
    }
    if (generalSettingData?.data.googleMapApiKey) {
      dispatch(
        handleSetErrorFields({
          errorFields: {
            errorValidGGApi: false,
          },
          isValidAPIKey: true,
        }),
      );
    }

    if (generalSettingData?.data.googleRoutesApiKey) {
      dispatch(
        handleSetErrorFields({
          errorFields: {
            errorValidGGRoutesApi: false,
          },
          isValidRoutesAPIKey: true,
        }),
      );
    }
  }, [dispatch, generalSettingData?.data]);

  useEffect(() => {
    if (emailSettingData?.data) {
      const emailSetting = {
        sender: emailSettingData.data.sender,
        accentColour: emailSettingData.data.accentColour || '#000000',
        logo: emailSettingData.data.logo,
        isVerifyEmail: emailSettingData.data.isVerifyEmail,
        isEnableReadyForPickup: emailSettingData.data.isEnableReadyForPickup,
        isEnableOutForDelivery: emailSettingData.data.isEnableOutForDelivery,
      };
      dispatch(handleSaveEmailBackupSettings(emailSetting));
      dispatch(handleSaveEmailSettings(emailSetting));
    }
  }, [emailSettingData?.data, dispatch]);

  const handleDiscard = () => {
    dispatch(handleResetSettings());
    dispatch(handleResetEmailSetting());
    setModal((prev) => ({ ...prev, isOpenDiscardModal: false }));
  };

  const handleLeavePage = () => {
    handleDiscard();
    setTimeout(() => {
      navigate({ pathname: navigatePath }, { replace: true });
    }, 100);
  };

  const handleNavigate = (path: string) => {
    if (isChangeData || isChangeEmailData) {
      setModal((prev) => ({ ...prev, isOpenDiscardModal: true }));
      setNavigatePath(path);
    } else {
      navigate(path, { replace: true });
    }
  };

  useTitleBar({ title: 'Settings' });

  return (
    <Layout>
      <AppSaveBar
        open={isChangeData || isChangeEmailData}
        disableDiscard={saveBar.isUploadingFile}
        onSave={handleSaveGeneralSettings}
        onDiscard={discardChange}
        loading={saveBar.isLoadingSaveBar || isUpdatingEmailSettings || isFetchingEmailSetting}
      />
      {isLoadingGetGeneralSetting ? (
        <SkeletonWrapper>
          <SkeletonLayout />
        </SkeletonWrapper>
      ) : (
        <InlineStack gap={'500'}>
          <StyledNav>
            <Box maxWidth="221px">
              <Card padding={'0'}>
                <Box background="bg-fill-secondary-active" padding={'200'}>
                  <InlineStack gap={'150'} blockAlign="center">
                    <StyledAvatar>{config.storeName.slice(0, 3)}</StyledAvatar>
                    <BoldSmallText fontWeight="medium">{config.storeName}</BoldSmallText>
                  </InlineStack>
                </Box>
                <Box padding={'400'}>
                  <BlockStack>
                    <Button
                      variant="tertiary"
                      textAlign="start"
                      icon={ButtonPressIcon}
                      onClick={() => handleNavigate(PATH.SETTINGS__CHECKOUT.pathname)}
                      pressed={location.pathname === PATH.SETTINGS__CHECKOUT.pathname}
                    >
                      {t('sections.checkoutValidation.title')}
                    </Button>
                    <Button
                      variant="tertiary"
                      textAlign="start"
                      icon={NotificationIcon}
                      onClick={() => handleNavigate(PATH.SETTINGS__EMAIL.pathname)}
                      pressed={location.pathname === PATH.SETTINGS__EMAIL.pathname}
                    >
                      {t('sections.emailNotification.title')}
                    </Button>
                    <Button
                      variant="tertiary"
                      textAlign="start"
                      icon={DeliveryIcon}
                      onClick={() => handleNavigate(PATH.SETTINGS__DELIVERY_VALIDATION.pathname)}
                      pressed={location.pathname === PATH.SETTINGS__DELIVERY_VALIDATION.pathname}
                    >
                      {t('sections.deliveryValidation.title')}
                    </Button>
                    <Button
                      variant="tertiary"
                      textAlign="start"
                      icon={OrdersStatusIcon}
                      onClick={() => handleNavigate(PATH.SETTINGS__ORDER_MANAGEMENT.pathname)}
                      pressed={location.pathname === PATH.SETTINGS__ORDER_MANAGEMENT.pathname}
                    >
                      {t('sections.orderManagement.title')}
                    </Button>
                    <Button
                      variant="tertiary"
                      textAlign="start"
                      icon={SettingsIcon}
                      onClick={() => handleNavigate(PATH.SETTINGS__ADVANCED_SETTING.pathname)}
                      pressed={location.pathname === PATH.SETTINGS__ADVANCED_SETTING.pathname}
                    >
                      {t('sections.advancedSetting.title')}
                    </Button>
                  </BlockStack>
                </Box>
              </Card>
            </Box>
          </StyledNav>
          <StyledFlexFullWidth>
            {location.pathname === PATH.SETTINGS__CHECKOUT.pathname && <CheckoutValidation />}
            {location.pathname === PATH.SETTINGS__EMAIL.pathname && <EmailNotification isLoading={isLoadingEmailSetting} />}
            {location.pathname === PATH.SETTINGS__DELIVERY_VALIDATION.pathname && <DeliveryValidation />}
            {location.pathname === PATH.SETTINGS__ORDER_MANAGEMENT.pathname && <OrderManagement />}
            {location.pathname === PATH.SETTINGS__ADVANCED_SETTING.pathname && <AdvanceSettings />}
          </StyledFlexFullWidth>
        </InlineStack>
      )}
      <DiscardChangeModal
        isOpen={modal.isOpenDiscardModal}
        onClose={() => setModal((prev) => ({ ...prev, isOpenDiscardModal: false }))}
        onStay={() => setModal((prev) => ({ ...prev, isOpenDiscardModal: false }))}
        onLeave={handleLeavePage}
      />
      <EmailVerifiedModal
        open={modal.isOpenVerifiedEmailModal}
        onCancel={() => setModal((prev) => ({ ...prev, isOpenVerifiedEmailModal: false }))}
      />
    </Layout>
  );
};

export default memo(GeneralSetting);
